import React, { useState } from "react";
import { Box, Container, Heading, VStack, Text } from "@chakra-ui/react";
import { format } from "date-fns";
import LocationFilter from "../components/LocationFilter";
import BookingCalendar from "../components/BookingCalendar";
import TimeSlotPicker from "../components/TimeSlotPicker";
import ConfirmationModal from "../components/ConfirmationModal";
import { TimeSlot, BookingFilters } from "../types";
import { locations, physicians, availableSlots } from "../data/mockData";

const BookAppointment: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [filters, setFilters] = useState<BookingFilters>({});
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Simplified toast function
  const showToast = (message: string) => {
    alert(message); // Simple fallback
  };

  // Filter slots based on selected filters
  const filteredSlots = availableSlots.filter((slot) => {
    if (filters.locationId && slot.locationId !== filters.locationId) {
      return false;
    }
    if (filters.physicianId && slot.physicianId !== filters.physicianId) {
      return false;
    }
    return true;
  });

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setIsModalOpen(true);
  };

  const handleConfirmBooking = () => {
    // In a real app, this would make an API call to book the appointment
    const message = `Your appointment has been scheduled for ${format(
      new Date(selectedSlot!.startTime),
      "MMMM d, yyyy"
    )} at ${format(new Date(selectedSlot!.startTime), "h:mm a")}.`;

    showToast(message);
    setIsModalOpen(false);
    setSelectedSlot(null);
  };

  return (
    <Container maxW="container.lg" py={8}>
      <VStack gap={6} align="stretch">
        <Heading as="h1" size="xl">
          Book an Appointment
        </Heading>

        <Box>
          <Box
            display="flex"
            borderBottom="1px solid"
            borderColor="gray.200"
            mb={4}
          >
            <Box
              px={4}
              py={2}
              cursor="pointer"
              borderBottom={activeTab === 0 ? "2px solid" : "none"}
              borderColor={activeTab === 0 ? "blue.500" : "transparent"}
              onClick={() => setActiveTab(0)}
            >
              Calendar View
            </Box>
            <Box
              px={4}
              py={2}
              cursor="pointer"
              borderBottom={activeTab === 1 ? "2px solid" : "none"}
              borderColor={activeTab === 1 ? "blue.500" : "transparent"}
              onClick={() => setActiveTab(1)}
            >
              List View
            </Box>
          </Box>

          {activeTab === 0 && (
            <VStack gap={6} align="stretch">
              <Box
                display="flex"
                flexDirection={{ base: "column", md: "row" }}
                gap={4}
              >
                <LocationFilter
                  locations={locations}
                  selectedLocation={filters.locationId}
                  onChange={(locationId) =>
                    setFilters({ ...filters, locationId })
                  }
                />

                <Box mb={2}>
                  <Text fontWeight="medium" mb={1}>
                    Select Physician
                  </Text>
                  <select
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "6px",
                      borderWidth: "1px",
                      borderColor: "#e2e8f0",
                      backgroundColor: "white",
                    }}
                    value={filters.physicianId || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFilters({
                        ...filters,
                        physicianId: value ? parseInt(value) : undefined,
                      });
                    }}
                  >
                    <option value="">All Physicians</option>
                    {physicians.map((physician) => (
                      <option key={physician.id} value={physician.id}>
                        {physician.name} ({physician.specialty})
                      </option>
                    ))}
                  </select>
                </Box>
              </Box>

              <BookingCalendar
                availableSlots={filteredSlots}
                onDateSelect={handleDateSelect}
              />

              <TimeSlotPicker
                date={selectedDate}
                timeSlots={filteredSlots}
                physicians={physicians}
                locations={locations}
                onSelectSlot={handleSlotSelect}
              />
            </VStack>
          )}

          {activeTab === 1 && (
            <VStack gap={6} align="stretch">
              <Box
                display="flex"
                flexDirection={{ base: "column", md: "row" }}
                gap={4}
              >
                <LocationFilter
                  locations={locations}
                  selectedLocation={filters.locationId}
                  onChange={(locationId) =>
                    setFilters({ ...filters, locationId })
                  }
                />

                <Box mb={2}>
                  <Text fontWeight="medium" mb={1}>
                    Select Physician
                  </Text>
                  <select
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "6px",
                      borderWidth: "1px",
                      borderColor: "#e2e8f0",
                      backgroundColor: "white",
                    }}
                    value={filters.physicianId || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFilters({
                        ...filters,
                        physicianId: value ? parseInt(value) : undefined,
                      });
                    }}
                  >
                    <option value="">All Physicians</option>
                    {physicians.map((physician) => (
                      <option key={physician.id} value={physician.id}>
                        {physician.name} ({physician.specialty})
                      </option>
                    ))}
                  </select>
                </Box>
              </Box>

              {/* Show next 7 days */}
              {Array.from({ length: 7 }).map((_, index) => {
                const date = new Date();
                date.setDate(date.getDate() + index);
                return (
                  <Box key={index} mt={index > 0 ? 8 : 0}>
                    <TimeSlotPicker
                      date={date}
                      timeSlots={filteredSlots}
                      physicians={physicians}
                      locations={locations}
                      onSelectSlot={handleSlotSelect}
                    />
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>
      </VStack>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedSlot={selectedSlot}
        physicians={physicians}
        locations={locations}
        onConfirm={handleConfirmBooking}
      />
    </Container>
  );
};

export default BookAppointment;
